import { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { 
  Calendar, 
  MapPin, 
  Clock, 
  Ticket,
  Users,
  Sparkles,
  ArrowLeft,
  Minus,
  Plus
} from "lucide-react";
import { Button } from "../components/ui/button";
import { PurchaseReview, QuoteSummary, usePurchaseQuote } from "../components/PurchaseReview";

const EventPage = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, token } = useAuth();
  const [event, setEvent] = useState(null);
  const [artist, setArtist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [reviewPurchase, setReviewPurchase] = useState(null);
  const quoteState = usePurchaseQuote([{ item_type: "ticket", item_id: eventId, quantity }], !!event);

  useEffect(() => {
    fetchEvent();
  }, [eventId]);

  const fetchEvent = async () => {
    setLoading(true);
    try {
      const eventRes = await axios.get(`${API}/events/${eventId}`);
      setEvent(eventRes.data);
      
      // Fetch artist
      const artistRes = await axios.get(`${API}/artists/${eventRes.data.artist_id}`);
      setArtist(artistRes.data);
    } catch (e) {
      console.error("Failed to fetch event:", e);
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async () => {
    if (!isAuthenticated) {
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
      const redirectUrl = window.location.origin + `/events/${eventId}`;
      window.location.href = "/auth?next=" + encodeURIComponent(window.location.pathname + window.location.search);
      return;
    }

    setReviewPurchase({ item_type: "ticket", item_id: eventId, quantity });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050810] flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen bg-[#050810] flex items-center justify-center">
        <div className="text-center">
          <Calendar className="w-16 h-16 text-[#8A92A0] mx-auto mb-4" />
          <h1 className="font-['Unbounded'] text-2xl text-white mb-2">Event Not Found</h1>
          <Link to="/discover" className="text-[#EC008C]">Browse Events</Link>
        </div>
      </div>
    );
  }

  const ticketsRemaining = event.tickets_available - event.tickets_sold;
  const totalPrice = (event.ticket_price * quantity).toFixed(2);

  return (
    <div className="min-h-screen bg-[#050810]" data-testid="event-page">
      <PurchaseReview purchase={reviewPurchase} onClose={() => setReviewPurchase(null)} />
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass-strong">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/home" className="flex items-center gap-2" data-testid="logo-link">
            <img src="/logo.png" alt="StardomFans" className="w-10 h-10 object-contain" style={{ mixBlendMode: "lighten" }} />
          </Link>
          <Button 
            variant="ghost" 
            onClick={() => navigate(-1)}
            className="text-[#8A92A0] hover:text-white"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
        </div>
      </header>

      {/* Event Hero */}
      <div className="pt-20 relative">
        <div 
          className="h-80 bg-cover bg-center"
          style={{ 
            backgroundImage: event.cover_image 
              ? `url(${event.cover_image})` 
              : `url(https://customer-assets.emergentagent.com/job_creator-vault-54/artifacts/8xs65yky_Sold-out%20concert%20frenzy%20under%20vibrant%20lights.png)`
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-t from-[#000000] via-[#000000]/70 to-transparent" />
        </div>
      </div>

      {/* Event Content */}
      <main className="max-w-5xl mx-auto px-6 -mt-32 relative z-10 pb-16">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Event Details */}
          <div className="lg:col-span-2 space-y-6">
            <div>
              <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium mb-4 ${
                event.status === "upcoming" ? "bg-[#EC008C]/10 text-[#EC008C]" : "bg-[#A1A1AA]/10 text-[#8A92A0]"
              }`}>
                {event.status}
              </span>
              <h1 className="font-['Unbounded'] text-4xl md:text-5xl font-bold text-white mb-4" data-testid="event-title">
                {event.title}
              </h1>
              
              {artist && (
                <Link 
                  to={`/artist/${artist.artist_id}`}
                  className="inline-flex items-center gap-3 mb-6 group"
                  data-testid="event-artist-link"
                >
                  <div className="w-12 h-12 rounded-full bg-[#1A2030] overflow-hidden">
                    {artist.profile_image ? (
                      <img src={artist.profile_image} alt={artist.stage_name} className="w-full h-full object-cover" />
                    ) : (
                      <Users className="w-full h-full p-2 text-[#8A92A0]" />
                    )}
                  </div>
                  <span className="text-white font-medium group-hover:text-[#EC008C] transition-colors">
                    {artist.stage_name}
                  </span>
                </Link>
              )}
            </div>

            <div className="card p-6 space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-[#EC008C]/10 flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-[#EC008C]" />
                </div>
                <div>
                  <p className="text-[#8A92A0] text-sm">Date</p>
                  <p className="text-white font-medium">{event.event_date}</p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-[#FF6BC5]/10 flex items-center justify-center">
                  <Clock className="w-6 h-6 text-[#FF6BC5]" />
                </div>
                <div>
                  <p className="text-[#8A92A0] text-sm">Time</p>
                  <p className="text-white font-medium">{event.event_time}</p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-[#D14A85]/10 flex items-center justify-center">
                  <MapPin className="w-6 h-6 text-[#D14A85]" />
                </div>
                <div>
                  <p className="text-[#8A92A0] text-sm">Venue</p>
                  <p className="text-white font-medium">{event.venue}</p>
                  <p className="text-[#8A92A0] text-sm">{event.location}</p>
                </div>
              </div>
            </div>

            {event.description && (
              <div className="card p-6">
                <h2 className="font-['Unbounded'] text-xl font-semibold text-white mb-4">About</h2>
                <p className="text-[#8A92A0] leading-relaxed">{event.description}</p>
              </div>
            )}
          </div>

          {/* Purchase Card */}
          <div className="lg:col-span-1">
            <div className="card p-6 sticky top-24" data-testid="purchase-card">
              <div className="mb-6">
                <p className="text-[#8A92A0] text-sm mb-1">Price per ticket</p>
                <p className="font-['Unbounded'] text-3xl font-bold text-[#EC008C]" data-testid="event-price">
                  {event.ticket_price} Tokens
                </p>
              </div>

              {/* Token-first economy — 1 Token = $1 USD, converted only at artist payout */}
              <div className="mb-6 rounded-lg border border-[#EC008C]/30 bg-[#EC008C]/[0.04] px-3 py-2 text-xs text-[#EC008C] font-semibold uppercase tracking-widest" data-testid="token-economy-label">
                Priced in Tokens · 1 Token = $1 USD
              </div>

              <div className="mb-6">
                <p className="text-[#8A92A0] text-sm mb-3">Quantity</p>
                <div className="flex items-center gap-4">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="border-[#1A2030] text-white hover:bg-[#0A0E18]"
                    disabled={quantity <= 1}
                    data-testid="quantity-minus-btn"
                  >
                    <Minus className="w-4 h-4" />
                  </Button>
                  <span className="text-white text-xl font-semibold w-12 text-center" data-testid="quantity-display">
                    {quantity}
                  </span>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setQuantity(Math.min(ticketsRemaining, quantity + 1))}
                    className="border-[#1A2030] text-white hover:bg-[#0A0E18]"
                    disabled={quantity >= ticketsRemaining}
                    data-testid="quantity-plus-btn"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div className="border-t border-[#1A2030] pt-4 mb-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[#8A92A0]">Subtotal</span>
                  <span className="text-white font-medium" data-testid="event-subtotal">{totalPrice} Tokens</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-[#8A92A0]">Tickets remaining</span>
                  <span className="text-[#8A92A0]">{ticketsRemaining}</span>
                </div>
              </div>

              <QuoteSummary state={quoteState} prefix="event" />
              <Button
                onClick={handlePurchase}
                disabled={purchasing || ticketsRemaining === 0 || quoteState.loading || !quoteState.quote}
                className="w-full bg-[#EC008C] text-[#000000] hover:bg-[#FF3399] font-semibold h-12 neon-primary"
                data-testid="purchase-btn"
              >
                {ticketsRemaining === 0 ? "Sold Out" : "Review tickets"}
              </Button>

              <p className="text-xs text-[#8A92A0] text-center mt-4">
                Secure checkout
              </p>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-[#1A2030]">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <img src="/logo.png" alt="SF" className="w-10 h-10 object-contain" style={{mixBlendMode:"lighten"}} />
          <p className="text-[#8A92A0] text-sm">&copy; {new Date().getFullYear()} StardomFans Ltd.</p>
        </div>
      </footer>
    </div>
  );
};

export default EventPage;
